import { useState, useRef } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import {
  ArrowLeft,
  Plus,
  Trash2,
  Zap,
  Save,
  ExternalLink,
  Upload,
  Pencil,
  Play,
  X,
} from 'lucide-react';
import { IllustrationEditor } from '@/components/illustration-editor';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@jeraisonne/ui';
import { SCHOOL_LEVELS, SUBJECTS } from '@jeraisonne/db';
import type { SchoolLevel, SubjectSlug } from '@jeraisonne/db';
import { supabase } from '@/lib/supabase';
import { useAdminEpreuveDetail, useAdminEpreuveJobs } from '@/lib/admin/epreuve-queries';
import {
  useUpdateEpreuve,
  useAddExerciseToEpreuve,
  useRemoveExerciseFromEpreuve,
  useReorderEpreuveExercises,
  useLaunchEpreuvePipeline,
} from '@/lib/admin/epreuve-mutations';
import type { AppEpreuveExercise } from '@/lib/student-objectives/objective-types';

const LEVEL_LABEL = Object.fromEntries(SCHOOL_LEVELS.map((l) => [l.value, l.label]));
const SUBJECT_LABEL = Object.fromEntries(SUBJECTS.map((s) => [s.value, s.label]));

const EXAM_TYPES = [
  { value: '', label: 'Non spécifié' },
  { value: 'brevet', label: 'Brevet' },
  { value: 'bac', label: 'Bac' },
  { value: 'bac_pro', label: 'Bac Pro' },
  { value: 'controle_commun', label: 'Contrôle commun' },
  { value: 'ds', label: 'DS' },
  { value: 'autre', label: 'Autre' },
] as const;
const EXAM_TYPE_LABEL = Object.fromEntries(EXAM_TYPES.map((t) => [t.value, t.label]));

const editInputCls =
  'border-ink-200 text-ink-900 placeholder:text-ink-400 focus:ring-primary-400 w-full rounded-xl border bg-white px-3 py-2.5 font-sans text-[14px] focus:outline-none focus:ring-2';
const editLabelCls = 'font-display text-ink-700 text-[12px] font-semibold';

const DIFFICULTY_LABEL = { easy: 'Facile', medium: 'Moyen', hard: 'Difficile' } as const;
const DIFFICULTY_CLS = {
  easy: 'bg-xp-50 text-xp-600',
  medium: 'bg-level-50 text-level-600',
  hard: 'bg-accent-50 text-accent-600',
} as const;

const STATUS_LABEL: Record<string, string> = {
  published: 'Publiée',
  draft: 'Brouillon',
  archived: 'Archivée',
};

// Phase courante d'un job (epreuve_jobs.current_step) → libellé lisible.
const JOB_STEP_LABEL: Record<string, string> = {
  statements: 'Énoncé',
  corrections: 'Corrigé',
  distractors: 'Distracteurs',
  finalizing: 'Finalisation',
};

const JOB_STATUS_LABEL: Record<string, string> = {
  queued: 'En file',
  processing: 'En cours',
  done: 'Terminé',
  failed: 'Échec',
  abandoned: 'Abandonné',
  cancelled: 'Annulé',
};

const JOB_DOT_CLS: Record<string, string> = {
  queued: 'bg-level-300',
  processing: 'bg-level-500 animate-pulse',
  done: 'bg-xp-500',
  failed: 'bg-accent-500',
  abandoned: 'bg-accent-400',
  cancelled: 'bg-level-300',
};

/** Libellé d'avancement d'un job : phase courante si en cours, sinon statut. */
function jobProgressLabel(status: string, currentStep: string | null): string {
  if (status === 'processing') return `${JOB_STEP_LABEL[currentStep ?? ''] ?? 'En cours'}…`;
  return JOB_STATUS_LABEL[status] ?? status;
}

// Simple exercise picker modal (search among published exercises)
function ExercisePickerModal({
  epreuveId,
  existingIds,
  onClose,
}: {
  epreuveId: string;
  existingIds: Set<string>;
  onClose: () => void;
}) {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<
    { id: string; title: string; difficulty: string; estimated_minutes: number | null }[]
  >([]);
  const [searching, setSearching] = useState(false);
  const addExercise = useAddExerciseToEpreuve();

  async function handleSearch() {
    if (!search.trim()) return;
    setSearching(true);
    const { data } = await supabase
      .from('exercises')
      .select('id, title, difficulty, estimated_minutes')
      .eq('status', 'published')
      .ilike('title', `%${search.trim()}%`)
      .limit(20);
    setResults(data ?? []);
    setSearching(false);
  }

  async function handleAdd(exerciseId: string) {
    await addExercise.mutateAsync({
      epreuveId,
      exerciseId,
      displayOrder: existingIds.size,
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center">
      <div className="w-full max-w-lg rounded-t-2xl bg-white p-5 shadow-xl sm:rounded-2xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-display text-ink-900 text-[17px] font-bold">Ajouter des exercices</h2>
          <button
            onClick={onClose}
            className="text-ink-400 hover:text-ink-700 font-sans text-[13px]"
          >
            Fermer
          </button>
        </div>

        <div className="mb-3 flex gap-2">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            placeholder="Rechercher par titre..."
            className="border-ink-200 text-ink-900 placeholder:text-ink-400 focus:ring-primary-400 flex-1 rounded-xl border px-3 py-2 font-sans text-[13px] focus:outline-none focus:ring-2"
          />
          <Button size="sm" onClick={handleSearch} disabled={searching}>
            {searching ? '…' : 'Chercher'}
          </Button>
        </div>

        <div className="max-h-64 space-y-1 overflow-y-auto">
          {results.length === 0 && !searching && (
            <p className="text-ink-400 py-4 text-center font-sans text-[13px]">
              Lancez une recherche pour afficher des exercices publiés.
            </p>
          )}
          {results.map((ex) => {
            const alreadyAdded = existingIds.has(ex.id);
            return (
              <div
                key={ex.id}
                className="flex items-center justify-between gap-3 rounded-xl px-3 py-2"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-ink-900 truncate font-sans text-[13px] font-medium">
                    {ex.title}
                  </p>
                  <span
                    className={`mt-0.5 inline-block rounded-full px-1.5 font-sans text-[10px] font-semibold ${
                      DIFFICULTY_CLS[ex.difficulty as keyof typeof DIFFICULTY_CLS] ??
                      'bg-ink-100 text-ink-600'
                    }`}
                  >
                    {DIFFICULTY_LABEL[ex.difficulty as keyof typeof DIFFICULTY_LABEL] ??
                      ex.difficulty}
                  </span>
                </div>
                <Button
                  size="sm"
                  variant={alreadyAdded ? 'outline' : 'primary'}
                  disabled={alreadyAdded || addExercise.isPending}
                  onClick={() => handleAdd(ex.id)}
                >
                  {alreadyAdded ? 'Ajouté' : <Plus className="h-3.5 w-3.5" />}
                </Button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export function TutorAdminEpreuveDetailPage() {
  const { epreuveId } = useParams({ strict: false }) as { epreuveId: string };
  const { data: epreuve, isLoading } = useAdminEpreuveDetail(epreuveId);
  const pipelineActive =
    epreuve?.pipeline_status === 'extracting' || epreuve?.pipeline_status === 'generating';
  const { data: jobs } = useAdminEpreuveJobs(epreuveId, pipelineActive);
  const updateEpreuve = useUpdateEpreuve();
  const removeExercise = useRemoveExerciseFromEpreuve();
  const reorder = useReorderEpreuveExercises();
  const launchPipeline = useLaunchEpreuvePipeline();
  const queryClient = useQueryClient();

  const [showPicker, setShowPicker] = useState(false);
  const [parsingPdf, setParsingPdf] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);
  const [uploadingCorrection, setUploadingCorrection] = useState(false);
  const correctionInputRef = useRef<HTMLInputElement>(null);
  const [editingInfo, setEditingInfo] = useState(false);
  const [form, setForm] = useState({
    title: '',
    description: '',
    level: '3e' as SchoolLevel,
    subject: '' as SubjectSlug | '',
    examType: '',
    examYear: '' as number | '',
  });

  /** Ouvre le panneau d'édition en repartant des valeurs actuelles de l'épreuve. */
  function openEditInfo() {
    if (!epreuve) return;
    setForm({
      title: epreuve.title,
      description: epreuve.description ?? '',
      level: epreuve.level,
      subject: epreuve.subject ?? '',
      examType: epreuve.examType ?? '',
      examYear: epreuve.examYear ?? '',
    });
    setEditingInfo(true);
  }

  async function handleSaveInfo(e: React.FormEvent) {
    e.preventDefault();
    if (!form.title.trim()) return;
    await updateEpreuve.mutateAsync({
      id: epreuveId,
      title: form.title.trim(),
      description: form.description.trim() || null,
      level: form.level,
      subject: form.subject || null,
      exam_type: form.examType || null,
      exam_year: form.examYear !== '' ? Number(form.examYear) : null,
    });
    setEditingInfo(false);
  }

  async function handleLaunchPipeline() {
    setParsingPdf(true);
    setParseError(null);

    try {
      await launchPipeline.mutateAsync(epreuveId);
    } catch (err) {
      setParseError(err instanceof Error ? err.message : 'Erreur au lancement du pipeline.');
      setParsingPdf(false);
      return;
    }

    // Polling de pipeline_status toutes les 4s jusqu'à done | failed (max 15 min :
    // extraction + corrigés + distracteurs de tous les exercices).
    const deadline = Date.now() + 15 * 60 * 1000;
    while (Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, 4000));
      const { data } = await supabase
        .from('epreuves')
        .select('pipeline_status, pipeline_error')
        .eq('id', epreuveId)
        .maybeSingle();
      queryClient.invalidateQueries({ queryKey: ['admin-epreuve-detail', epreuveId] });
      queryClient.invalidateQueries({ queryKey: ['admin-epreuve-jobs', epreuveId] });

      const status = (data as { pipeline_status?: string } | null)?.pipeline_status;
      if (status === 'done') break;
      if (status === 'failed') {
        setParseError(
          (data as { pipeline_error?: string } | null)?.pipeline_error ??
            'Le pipeline a échoué. Vérifiez les logs des edge functions.',
        );
        break;
      }
    }

    queryClient.invalidateQueries({ queryKey: ['admin-epreuves'] });
    queryClient.invalidateQueries({ queryKey: ['admin-epreuve-jobs', epreuveId] });
    setParsingPdf(false);
  }

  /** Attache (ou remplace) le PDF du corrigé a posteriori. */
  async function handleUploadCorrection(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0] ?? null;
    if (correctionInputRef.current) correctionInputRef.current.value = '';
    if (!file) return;
    if (file.type !== 'application/pdf') {
      setParseError('Seuls les PDF sont acceptés pour le corrigé.');
      return;
    }
    setUploadingCorrection(true);
    setParseError(null);
    try {
      const fileName = `corr_${Date.now()}_${file.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
      const { error: upErr } = await supabase.storage
        .from('epreuves-pdf')
        .upload(fileName, file, { contentType: 'application/pdf' });
      if (upErr) throw new Error(upErr.message);
      await updateEpreuve.mutateAsync({ id: epreuveId, correction_file_url: fileName });
    } catch (err) {
      setParseError(err instanceof Error ? err.message : 'Erreur upload corrigé.');
    } finally {
      setUploadingCorrection(false);
    }
  }

  async function handleMoveUp(exercises: AppEpreuveExercise[], idx: number) {
    if (idx === 0) return;
    const ids = exercises.map((e) => e.exerciseId);
    [ids[idx - 1], ids[idx]] = [ids[idx]!, ids[idx - 1]!];
    await reorder.mutateAsync({ epreuveId, orderedExerciseIds: ids });
  }

  async function handleMoveDown(exercises: AppEpreuveExercise[], idx: number) {
    if (idx === exercises.length - 1) return;
    const ids = exercises.map((e) => e.exerciseId);
    [ids[idx], ids[idx + 1]] = [ids[idx + 1]!, ids[idx]!];
    await reorder.mutateAsync({ epreuveId, orderedExerciseIds: ids });
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="border-primary-500 h-8 w-8 animate-spin rounded-full border-2 border-t-transparent" />
      </div>
    );
  }

  if (!epreuve) {
    return (
      <div className="mx-auto max-w-3xl p-4 sm:p-6">
        <p className="text-ink-500">Épreuve introuvable.</p>
      </div>
    );
  }

  const exercises = epreuve.exercises ?? [];
  const existingIds = new Set(exercises.map((e) => e.exerciseId));

  const isPipelineRunning =
    parsingPdf ||
    epreuve.pipeline_status === 'extracting' ||
    epreuve.pipeline_status === 'generating';

  const sourceFileUrl = epreuve.sourceFileUrl;

  async function handleOpenPdf() {
    if (!sourceFileUrl) return;
    const { data } = await supabase.storage
      .from('epreuves-pdf')
      .createSignedUrl(sourceFileUrl, 300);
    if (data?.signedUrl) window.open(data.signedUrl, '_blank', 'noopener,noreferrer');
  }

  return (
    <div className="mx-auto max-w-3xl p-4 sm:p-6">
      <Link
        to="/tutor/admin/epreuves"
        className="text-ink-500 hover:text-ink-900 mb-5 inline-flex items-center gap-1.5 font-sans text-[13px] transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Épreuves
      </Link>

      {/* Header */}
      <div className="mb-6 flex items-start gap-3">
        <h1 className="font-display text-ink-900 min-w-0 flex-1 text-[22px] font-extrabold">
          {epreuve.title}
        </h1>
        {!editingInfo && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="shrink-0 gap-1.5"
            onClick={openEditInfo}
          >
            <Pencil className="h-4 w-4" />
            Modifier
          </Button>
        )}
      </div>

      {editingInfo ? (
        /* Panneau d'édition des infos */
        <form
          onSubmit={handleSaveInfo}
          className="border-ink-200 mb-6 space-y-4 rounded-2xl border bg-white p-4 sm:p-5"
        >
          <div className="flex flex-col gap-1.5">
            <label className={editLabelCls}>Titre</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              className={editInputCls}
              required
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className={editLabelCls}>
              Description <span className="text-ink-400 font-normal">(optionnel)</span>
            </label>
            <textarea
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              rows={2}
              className={`${editInputCls} resize-none`}
            />
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <label className={editLabelCls}>Niveau</label>
              <select
                value={form.level}
                onChange={(e) => setForm((f) => ({ ...f, level: e.target.value as SchoolLevel }))}
                className={editInputCls}
              >
                {SCHOOL_LEVELS.map((l) => (
                  <option key={l.value} value={l.value}>
                    {l.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <label className={editLabelCls}>
                Matière <span className="text-ink-400 font-normal">(optionnel)</span>
              </label>
              <select
                value={form.subject}
                onChange={(e) =>
                  setForm((f) => ({ ...f, subject: e.target.value as SubjectSlug | '' }))
                }
                className={editInputCls}
              >
                <option value="">Toutes</option>
                {SUBJECTS.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <label className={editLabelCls}>Type d'épreuve</label>
              <select
                value={form.examType}
                onChange={(e) => setForm((f) => ({ ...f, examType: e.target.value }))}
                className={editInputCls}
              >
                {EXAM_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <label className={editLabelCls}>
                Année <span className="text-ink-400 font-normal">(optionnel)</span>
              </label>
              <input
                type="number"
                min={1990}
                max={2100}
                placeholder="2024"
                value={form.examYear}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    examYear: e.target.value ? parseInt(e.target.value, 10) : '',
                  }))
                }
                className={editInputCls}
              />
            </div>
          </div>

          <div className="flex flex-col-reverse gap-2 pt-1 sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant="outline"
              className="w-full gap-1.5 sm:w-auto"
              onClick={() => setEditingInfo(false)}
              disabled={updateEpreuve.isPending}
            >
              <X className="h-4 w-4" />
              Annuler
            </Button>
            <Button
              type="submit"
              className="w-full gap-1.5 sm:w-auto"
              disabled={updateEpreuve.isPending || !form.title.trim()}
            >
              <Save className="h-4 w-4" />
              {updateEpreuve.isPending ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
          </div>
        </form>
      ) : (
        /* Méta + statut (lecture) */
        <div className="bg-ink-50 mb-6 flex flex-wrap items-center gap-3 rounded-xl px-4 py-3">
          <span className="text-ink-600 font-sans text-[13px]">
            {LEVEL_LABEL[epreuve.level] ?? epreuve.level}
          </span>
          {epreuve.subject && (
            <span className="text-ink-600 font-sans text-[13px]">
              {SUBJECT_LABEL[epreuve.subject] ?? epreuve.subject}
            </span>
          )}
          {epreuve.examType && (
            <span className="text-ink-600 font-sans text-[13px]">
              {EXAM_TYPE_LABEL[epreuve.examType] ?? epreuve.examType}
              {epreuve.examYear ? ` ${epreuve.examYear}` : ''}
            </span>
          )}
          <span className="text-ink-600 font-sans text-[13px]">
            {exercises.length} exercice{exercises.length !== 1 ? 's' : ''}
          </span>
          <div className="ml-auto flex items-center gap-2">
            {sourceFileUrl && (
              <button
                onClick={handleOpenPdf}
                className="text-ink-500 hover:text-ink-900 inline-flex items-center gap-1 font-sans text-[12px] transition-colors"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                Voir le PDF
              </button>
            )}
            <span className="text-ink-500 font-sans text-[12px]">Statut :</span>
            <select
              value={epreuve.status}
              onChange={(e) =>
                updateEpreuve.mutate({
                  id: epreuveId,
                  status: e.target.value as 'draft' | 'published' | 'archived',
                })
              }
              className="border-ink-200 focus:ring-primary-400 rounded-lg border bg-white px-2 py-1 font-sans text-[12px] focus:outline-none focus:ring-1"
            >
              <option value="draft">Brouillon</option>
              <option value="published">Publiée</option>
              <option value="archived">Archivée</option>
            </select>
          </div>
        </div>
      )}

      {/* Pipeline d'extraction V2 */}
      {epreuve.sourceFileUrl && (
        <div className="border-level-100 bg-level-50 mb-6 rounded-xl border px-4 py-3">
          <div className="flex items-center gap-3">
            <Zap
              className={`h-5 w-5 shrink-0 ${
                isPipelineRunning ? 'text-level-400 animate-pulse' : 'text-level-500'
              }`}
            />
            <div className="flex-1">
              <p className="text-level-800 font-sans text-[13px] font-medium">
                Pipeline d'extraction → exercices V2
              </p>
              <p className="text-level-600 font-sans text-[11px]">
                {epreuve.pipeline_status === 'extracting'
                  ? 'Segmentation du sujet… (repérage des exercices)'
                  : epreuve.pipeline_status === 'generating'
                    ? `Extraction par exercice (énoncé → corrigé → distracteurs)… ${epreuve.jobs_done}/${epreuve.jobs_total} terminé(s)`
                    : epreuve.pipeline_status === 'done'
                      ? `Terminé — ${epreuve.jobs_done}/${epreuve.jobs_total} exercice(s) générés.`
                      : epreuve.correction_file_url
                        ? 'Énoncé + corrigé prêts. Lance le pipeline pour décomposer en V2.'
                        : 'Énoncé prêt. Ajoute le corrigé (recommandé) puis lance le pipeline.'}
              </p>
            </div>
            {!isPipelineRunning && epreuve.pipeline_status !== 'done' && (
              <Button size="sm" onClick={handleLaunchPipeline}>
                {epreuve.pipeline_status === 'failed' ? 'Relancer les échecs' : 'Lancer'}
              </Button>
            )}
          </div>

          {epreuve.pipeline_status === 'generating' && epreuve.jobs_total > 0 && (
            <div className="bg-level-100 mt-3 h-1.5 w-full overflow-hidden rounded-full">
              <div
                className="bg-level-500 h-full rounded-full transition-all"
                style={{ width: `${Math.round((epreuve.jobs_done / epreuve.jobs_total) * 100)}%` }}
              />
            </div>
          )}

          {/* Avancement par exercice : on sait à quelle étape on est. */}
          {(jobs ?? []).length > 0 && epreuve.pipeline_status !== 'done' && (
            <ul className="mt-3 space-y-1">
              {(jobs ?? []).map((j) => (
                <li
                  key={j.id}
                  className="flex items-center gap-2 font-sans text-[12px] leading-snug"
                >
                  <span
                    className={`h-2 w-2 shrink-0 rounded-full ${JOB_DOT_CLS[j.status] ?? 'bg-level-300'}`}
                  />
                  <span className="text-level-800 min-w-0 flex-1 truncate">{j.exerciseTitle}</span>
                  <span
                    className={`shrink-0 ${j.status === 'failed' || j.status === 'abandoned' ? 'text-accent-600' : 'text-level-500'}`}
                  >
                    {jobProgressLabel(j.status, j.currentStep)}
                  </span>
                </li>
              ))}
            </ul>
          )}

          {/* Corrigé : statut + upload a posteriori */}
          <div className="border-level-100 mt-3 flex items-center gap-2 border-t pt-3">
            <span className="text-level-700 font-sans text-[12px]">
              Corrigé : {epreuve.correction_file_url ? '✓ joint' : 'aucun'}
            </span>
            {!isPipelineRunning && (
              <button
                type="button"
                onClick={() => correctionInputRef.current?.click()}
                disabled={uploadingCorrection}
                className="text-level-600 hover:text-level-900 ml-auto inline-flex items-center gap-1 font-sans text-[12px] transition-colors disabled:opacity-50"
              >
                <Upload className="h-3.5 w-3.5" />
                {uploadingCorrection
                  ? 'Envoi…'
                  : epreuve.correction_file_url
                    ? 'Remplacer'
                    : 'Ajouter le corrigé'}
              </button>
            )}
            <input
              ref={correctionInputRef}
              type="file"
              accept="application/pdf"
              className="hidden"
              onChange={handleUploadCorrection}
            />
          </div>
        </div>
      )}
      {parseError && <p className="text-accent-600 mb-4 font-sans text-[13px]">{parseError}</p>}

      {/* Détail des erreurs par exercice */}
      {(() => {
        const failedJobs = (jobs ?? []).filter(
          (j) => j.status === 'failed' || j.status === 'abandoned',
        );
        if (failedJobs.length === 0) return null;
        return (
          <div className="border-accent-100 bg-accent-50 mb-6 rounded-xl border px-4 py-3">
            <p className="text-accent-800 mb-2 font-sans text-[13px] font-semibold">
              {failedJobs.length} exercice{failedJobs.length > 1 ? 's' : ''} en échec
            </p>
            <ul className="space-y-2">
              {failedJobs.map((j) => (
                <li key={j.id} className="font-sans text-[12px]">
                  <span className="text-accent-700 font-medium">{j.exerciseTitle}</span>
                  {j.restartCount > 0 && (
                    <span className="text-accent-500"> · {j.restartCount} relance(s)</span>
                  )}
                  <p className="text-accent-600 wrap-break-word mt-0.5 font-mono text-[11px] leading-snug">
                    {j.error ?? 'Erreur inconnue (voir les logs de la fonction).'}
                  </p>
                </li>
              ))}
            </ul>
            <p className="text-accent-500 mt-2 font-sans text-[11px]">
              « Relancer » relance le pipeline. Astuce : un échec de lecture LLM (JSON tronqué) se
              résout souvent en relançant ; un dépassement de budget attend la fenêtre de 24 h.
            </p>
          </div>
        );
      })()}

      {/* Illustration */}
      <div className="mb-6">
        <IllustrationEditor
          entityType="epreuve"
          entityId={epreuveId}
          initialUrl={epreuve.illustration_url}
          initialPrompt={epreuve.illustration_prompt}
          level={epreuve.level}
          title={epreuve.title}
        />
      </div>

      {/* Liste des exercices */}
      <div className="mb-4 flex items-center justify-between gap-3">
        <h2 className="font-display text-ink-800 text-[16px] font-bold">Exercices</h2>
        <Button size="sm" className="gap-1.5" onClick={() => setShowPicker(true)}>
          <Plus className="h-4 w-4" />
          Ajouter
        </Button>
      </div>

      {exercises.length === 0 ? (
        <div className="border-ink-100 rounded-2xl border py-12 text-center">
          <p className="text-ink-400 font-sans text-[13px]">
            Aucun exercice. Ajoutez-en ou analysez le PDF.
          </p>
        </div>
      ) : (
        <div className="border-ink-200 divide-ink-100 divide-y overflow-hidden rounded-2xl border bg-white">
          {exercises.map((ee, idx) => (
            <div key={ee.exerciseId} className="flex items-center gap-3 px-3 py-2.5">
              {/* Ordre */}
              <span className="text-ink-300 w-5 shrink-0 text-center font-sans text-[12px]">
                {idx + 1}
              </span>

              {/* Info — le titre ouvre la fiche de l'exercice (édition / test) */}
              <div className="min-w-0 flex-1">
                <Link
                  to="/tutor/exercises/$exerciseId"
                  params={{ exerciseId: ee.exerciseId }}
                  className="text-ink-900 hover:text-primary-600 block truncate font-sans text-[13px] font-medium transition-colors"
                >
                  {ee.exercise.title}
                </Link>
                <div className="mt-0.5 flex items-center gap-1.5">
                  <span
                    className={`rounded-full px-1.5 font-sans text-[10px] font-semibold ${
                      DIFFICULTY_CLS[ee.exercise.difficulty as keyof typeof DIFFICULTY_CLS] ??
                      'bg-ink-100 text-ink-600'
                    }`}
                  >
                    {DIFFICULTY_LABEL[ee.exercise.difficulty as keyof typeof DIFFICULTY_LABEL] ??
                      ee.exercise.difficulty}
                  </span>
                  {ee.exercise.estimatedMinutes && (
                    <span className="text-ink-400 font-sans text-[10px]">
                      {ee.exercise.estimatedMinutes} min
                    </span>
                  )}
                </div>
              </div>

              {/* Actions : modifier (V2) + tester */}
              <Link
                to="/tutor/exercises/$exerciseId/edit-v2"
                params={{ exerciseId: ee.exerciseId }}
                className="text-ink-400 hover:text-primary-600 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition-colors"
                aria-label="Modifier l'exercice"
                title="Modifier (éditeur V2)"
              >
                <Pencil className="h-4 w-4" />
              </Link>
              <Link
                to="/tutor/exercises/$exerciseId/test"
                params={{ exerciseId: ee.exerciseId }}
                className="text-ink-400 hover:text-level-600 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition-colors"
                aria-label="Tester l'exercice"
                title="Tester"
              >
                <Play className="h-4 w-4" />
              </Link>

              {/* Réordonner */}
              <div className="flex flex-col gap-0.5">
                <button
                  disabled={idx === 0 || reorder.isPending}
                  onClick={() => handleMoveUp(exercises, idx)}
                  className="text-ink-300 hover:text-ink-600 font-sans text-[10px] leading-none disabled:opacity-20"
                  aria-label="Monter"
                >
                  ▲
                </button>
                <button
                  disabled={idx === exercises.length - 1 || reorder.isPending}
                  onClick={() => handleMoveDown(exercises, idx)}
                  className="text-ink-300 hover:text-ink-600 font-sans text-[10px] leading-none disabled:opacity-20"
                  aria-label="Descendre"
                >
                  ▼
                </button>
              </div>

              {/* Supprimer */}
              <button
                onClick={() => removeExercise.mutate({ epreuveId, exerciseId: ee.exerciseId })}
                disabled={removeExercise.isPending}
                className="text-ink-300 hover:text-accent-500 shrink-0 transition-colors disabled:opacity-50"
                aria-label="Retirer"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {showPicker && (
        <ExercisePickerModal
          epreuveId={epreuveId}
          existingIds={existingIds}
          onClose={() => setShowPicker(false)}
        />
      )}
    </div>
  );
}
